import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { dashboardAPI } from '../services/api';
import { Spinner } from '../components/Spinner';
import { formatCurrency } from '../utils/formatters';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  ShoppingCart,
  Package,
  Truck,
  Users,
  AlertCircle,
  BarChart3,
  PieChart,
  Activity
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar
} from 'recharts';

const Dashboard = () => {
  const { isDark } = useTheme();
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  const [salesChart, setSalesChart] = useState([]);
  const [expensesChart, setExpensesChart] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [loading, setLoading] = useState(true);

  const isAdmin = user?.role?.toLowerCase() === 'admin';

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const [
          statsRes,
          salesRes,
          expensesRes,
          activitiesRes,
          productsRes,
          stockRes
        ] = await Promise.all([
          dashboardAPI.getStats(),
          dashboardAPI.getSalesChart(),
          dashboardAPI.getExpensesChart(),
          dashboardAPI.getRecentActivities(),
          dashboardAPI.getTopProducts(),
          dashboardAPI.getLowStock()
        ]);

        setStats(statsRes.data);
        setSalesChart(salesRes.data);
        setExpensesChart(expensesRes.data);
        setRecentActivities(activitiesRes.data);
        setTopProducts(productsRes.data);
        setLowStock(stockRes.data);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const colorMap = {
    green: 'bg-green-100 text-green-600',
    red: 'bg-red-100 text-red-600',
    yellow: 'bg-yellow-100 text-yellow-600',
    blue: 'bg-blue-100 text-blue-600',
    purple: 'bg-purple-100 text-purple-600',
    indigo: 'bg-indigo-100 text-indigo-600',
    orange: 'bg-orange-100 text-orange-600'
  };

  const StatCard = ({ title, value, icon: Icon, change, changeType, color = 'blue' }) => (
    <div className={`${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-slate-200'} rounded-xl border p-5 shadow-sm`}>
      <div className="flex items-start justify-between">
        <div>
          <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>{title}</p>
          <p className={`mt-2 text-2xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{value}</p>
          {change && (
            <div className={`flex items-center mt-1 text-sm ${
              changeType === 'increase' ? 'text-green-600' : 'text-red-600'
            }`}>
              {changeType === 'increase' ? (
                <TrendingUp className="w-4 h-4 mr-1" />
              ) : (
                <TrendingDown className="w-4 h-4 mr-1" />
              )}
              {change}
            </div>
          )}
        </div>
        <div className={`rounded-xl p-3 ${isDark ? `bg-${color}-900/30` : colorMap[color] || colorMap.blue}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className={`rounded-2xl ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-slate-200'} border p-6 shadow-sm`}>
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>Panel</h1>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>İşletme genel durumu ve son hareketler burada yer alır.</p>
          </div>
          {isAdmin && (
            <div className="grid grid-cols-2 gap-3 md:w-auto">
              <div className={`rounded-xl ${isDark ? 'bg-gray-700' : 'bg-slate-50'} px-4 py-3`}>
                <p className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>Bugünkü Satış</p>
                <p className={`mt-1 text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{formatCurrency(stats.dailySales)}</p>
              </div>
              <div className={`rounded-xl ${isDark ? 'bg-gray-700' : 'bg-slate-50'} px-4 py-3`}>
                <p className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>Açık Alacak</p>
                <p className={`mt-1 text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{formatCurrency(stats.totalReceivables)}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {isAdmin && (
          <>
            <StatCard
              title="Günlük Satışlar"
              value={formatCurrency(stats.dailySales)}
              icon={DollarSign}
              color="green"
            />
            <StatCard
              title="Günlük Giderler"
              value={formatCurrency(stats.dailyExpenses)}
              icon={TrendingDown}
              color="red"
            />
          </>
        )}
        <StatCard
          title="Bekleyen Siparişler"
          value={stats.pendingOrders || 0}
          icon={ShoppingCart}
          color="yellow"
        />
        <StatCard
          title="Gönderilen Siparişler"
          value={stats.shippedOrders || 0}
          icon={Truck}
          color="blue"
        />
        <StatCard
          title="Toplam Müşteriler"
          value={stats.totalCustomers || 0}
          icon={Users}
          color="purple"
        />
        <StatCard
          title="Toplam Ürünler"
          value={stats.totalProducts || 0}
          icon={Package}
          color="indigo"
        />
        {isAdmin && (
          <StatCard
            title="Toplam Alacaklar"
            value={formatCurrency(stats.totalReceivables)}
            icon={DollarSign}
            color="green"
          />
        )}
        <StatCard
          title="Bekleyen Üretim"
          value={stats.productionPending || 0}
          icon={Activity}
          color="orange"
        />
      </div>

      {isAdmin && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className={`${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-slate-200'} p-6 rounded-2xl border shadow-sm`}>
            <h3 className={`text-lg font-semibold mb-4 flex items-center ${isDark ? 'text-white' : 'text-slate-900'}`}>
              <BarChart3 className="w-5 h-5 mr-2" />
              Satış Trendi (30 Gün)
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={salesChart}>
                <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : '#e2e8f0'} />
                <XAxis dataKey="date" stroke={isDark ? '#9ca3af' : '#64748b'} />
                <YAxis stroke={isDark ? '#9ca3af' : '#64748b'} />
                <Tooltip 
                  formatter={(value) => formatCurrency(value)}
                  contentStyle={{
                    backgroundColor: isDark ? '#1f2937' : '#ffffff',
                    border: isDark ? '1px solid #374151' : '1px solid #e2e8f0',
                    color: isDark ? '#ffffff' : '#000000'
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="total" 
                  stroke="#3b82f6" 
                  strokeWidth={2}
                  dot={{ fill: '#3b82f6' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className={`${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-slate-200'} p-6 rounded-2xl border shadow-sm`}>
            <h3 className={`text-lg font-semibold mb-4 flex items-center ${isDark ? 'text-white' : 'text-slate-900'}`}>
              <PieChart className="w-5 h-5 mr-2" />
              Gider Trendi (30 Gün)
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={expensesChart}>
                <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : '#e2e8f0'} />
                <XAxis dataKey="date" stroke={isDark ? '#9ca3af' : '#64748b'} />
                <YAxis stroke={isDark ? '#9ca3af' : '#64748b'} />
                <Tooltip 
                  formatter={(value) => formatCurrency(value)}
                  contentStyle={{
                    backgroundColor: isDark ? '#1f2937' : '#ffffff',
                    border: isDark ? '1px solid #374151' : '1px solid #e2e8f0',
                    color: isDark ? '#ffffff' : '#000000'
                  }}
                />
                <Bar dataKey="total" fill="#ef4444" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className={`${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-slate-200'} p-6 rounded-2xl border shadow-sm`}>
          <h2 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>Son Aktiviteler</h2>
          <div className="space-y-3">
            {recentActivities.slice(0, 5).map((activity, index) => (
              <div key={index} className={`flex items-start space-x-3 rounded-xl ${isDark ? 'bg-gray-700' : 'bg-slate-50'} px-3 py-3`}>
                <div className="mt-1 w-2 h-2 bg-blue-500 rounded-full"></div>
                <div className="flex-1">
                  <p className={`text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>{activity.description}</p>
                  <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                    {new Date(activity.created_at).toLocaleString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className={`${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-slate-200'} p-6 rounded-2xl border shadow-sm`}>
          <h2 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>En Çok Satan Ürünler</h2>
          <div className="space-y-3">
            {topProducts.slice(0, 5).map((product, index) => (
              <div key={index} className={`flex justify-between items-center rounded-xl ${isDark ? 'bg-gray-700' : 'bg-slate-50'} px-3 py-3`}>
                <div>
                  <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{product.name}</p>
                  <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>{product.sku}</p>
                </div>
                <div className="text-right">
                  <p className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{product.total_sold}</p>
                  <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>satıldı</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className={`${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-slate-200'} p-6 rounded-2xl border shadow-sm`}>
          <h3 className={`text-lg font-semibold mb-4 flex items-center ${isDark ? 'text-white' : 'text-slate-900'}`}>
            <AlertCircle className="w-5 h-5 mr-2 text-yellow-500" />
            Stok Uyarısı
          </h3>
          <div className="space-y-3">
            {lowStock.length > 0 ? (
              lowStock.slice(0, 5).map((product, index) => (
                <div key={index} className={`flex justify-between items-center rounded-xl ${isDark ? 'bg-gray-700' : 'bg-slate-50'} px-3 py-3`}>
                  <div>
                    <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{product.name}</p>
                    <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>{product.sku}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-red-600">{product.stock_quantity}</p>
                    <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>adet</p>
                  </div>
                </div>
              ))
            ) : (
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>Tüm ürünlerin stoğu yeterli.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
